import { OVERVIEW_TILES } from '../overview-tiles.ts';
import { WIDGET_PRESETS, widgetFromPreset, type WidgetPreset } from './widget-presets.ts';
import { LAYOUT_SCHEMA_VERSION, type DashboardLayout, type DashboardWidget } from './widget-types.ts';

type KpiTileKey = Extract<DashboardWidget, { kind: 'kpi' }>['settings']['tile'];

const kpiWidgetOf = (tile: KpiTileKey): DashboardWidget => ({
  id: `kpi-${tile}`,
  kind: 'kpi',
  size: 'sm',
  title: null,
  settings: { tile },
});

const FIXED_TILES: readonly KpiTileKey[] = [
  'totalSales',
  'deliveredSales',
  'pendingSales',
  'totalReturns',
  'netSalesGain',
  'netProfit',
  'stockPurchaseValue',
  'stockSaleValue',
  'products',
  'depots',
];

const FIXED_CHARTS: readonly string[] = [
  'salesTrend',
  'funnel',
  'statusesByDay',
  'topProducts',
  'ordersByGovernorate',
  'deliveryRateByCarrier',
];

const PRESETS_BY_KEY: ReadonlyMap<string, WidgetPreset> = new Map(
  Object.values(WIDGET_PRESETS)
    .flat()
    .map((preset) => [preset.key, preset]),
);

const chartWidgetOf = (key: string): DashboardWidget => {
  const preset = PRESETS_BY_KEY.get(key);
  if (preset === undefined) throw new RangeError(`unknown dashboard preset ${key}`);
  return widgetFromPreset(preset, key);
};

const isKpiTile = (key: string): key is KpiTileKey => OVERVIEW_TILES.some((tile) => tile.key === key);

const layoutWithTiles = (tiles: readonly string[]): DashboardLayout => ({
  schemaVersion: LAYOUT_SCHEMA_VERSION,
  widgets: [...tiles.filter(isKpiTile).map(kpiWidgetOf), ...FIXED_CHARTS.map(chartWidgetOf)],
});

export const DEFAULT_LAYOUT: DashboardLayout = layoutWithTiles(FIXED_TILES);

export const LEGACY_TILES_KEY = 'admin.dashboard.tiles';

const shownTilesIn = (stored: unknown): readonly string[] | undefined => {
  if (typeof stored !== 'object' || stored === null || !('state' in stored)) return undefined;
  const state: unknown = stored.state;
  if (typeof state !== 'object' || state === null || !('shownTiles' in state)) return undefined;
  return Array.isArray(state.shownTiles)
    ? state.shownTiles.filter((key): key is string => typeof key === 'string')
    : undefined;
};

const parsedOrNull = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const standardLayoutOf = (legacyTiles: string | null): DashboardLayout => {
  const chosen = legacyTiles === null ? undefined : shownTilesIn(parsedOrNull(legacyTiles));
  return chosen === undefined ? DEFAULT_LAYOUT : layoutWithTiles(chosen);
};
